
// Helper functions for Google Analytics and Google Tag Manager
import { hasConsent } from './cookieConsent';

// Google Analytics Measurement ID
export const GA_MEASUREMENT_ID = 'G-3Z2L0GSK0W';

// Initialize page view tracking for react-router
export const initializeAnalytics = () => {
  // Only initialize if needed - lazy load approach
  if (typeof window !== 'undefined' && window.dataLayer && !window._analyticsInitialized) {
    window._analyticsInitialized = true;
    console.log('Google Analytics initialized with ID:', GA_MEASUREMENT_ID);
    
    // Ensure correct measurement ID is set - only when consent is given
    if (window.gtag && hasConsent('analytics')) {
      window.gtag('config', GA_MEASUREMENT_ID, { 
        send_page_view: false // We'll send pageviews manually
      });
    }
  }
};

// Debug helper to verify GA is properly connected - only runs in development
export const verifyAnalyticsConnection = () => {
  if (process.env.NODE_ENV !== 'production' && typeof window !== 'undefined') {
    // Check if GA script is loaded
    const gaScriptExists = !!document.querySelector(`script[src*="gtag/js?id=${GA_MEASUREMENT_ID}"]`);
    console.log(`Google Analytics script found in DOM: ${gaScriptExists}`);
    
    // Check if gtag function exists
    console.log(`Google Analytics gtag function exists: ${typeof window.gtag === 'function'}`);
    
    // Check if dataLayer exists
    console.log(`Google Analytics dataLayer exists: ${!!(window.dataLayer && Array.isArray(window.dataLayer))}`);
    
    // Log consent status
    console.log(`Analytics consent status: ${hasConsent('analytics')}`);
  }
};

// Track page views - optimized to reduce unnecessary calls
export const pageView = (path: string) => {
  // Debounce page view tracking using requestIdleCallback
  if (typeof window !== 'undefined' && window.dataLayer && hasConsent('analytics')) {
    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(() => {
        sendPageView(path);
      }, { timeout: 2000 });
    } else {
      // Fallback for browsers without requestIdleCallback
      setTimeout(() => sendPageView(path), 100);
    }
  }
};

// Helper function to actually send the page view
const sendPageView = (path: string) => {
  if (window.gtag) {
    window.gtag('event', 'page_view', {
      page_path: path,
      send_to: GA_MEASUREMENT_ID
    });
  }
  
  // Only log in development mode
  if (process.env.NODE_ENV !== 'production') {
    console.log(`Page view tracked: ${path}`);
  }
};

// Track custom events - optimized
export const event = ({ action, category, label, value }: {
  action: string;
  category: string;
  label?: string;
  value?: number;
}) => {
  if (typeof window !== 'undefined' && hasConsent('analytics')) {
    // Delay non-critical events to prioritize UI rendering
    setTimeout(() => {
      if (window.gtag) {
        window.gtag('event', action, {
          event_category: category,
          event_label: label,
          value: value,
          send_to: GA_MEASUREMENT_ID
        });
      }
    }, 100);
  }
};

// Track SEO-related events specifically for Search Console insights - optimized
export const trackSEOEvent = ({ action, source, page }: {
  action: 'impression' | 'click' | 'position_change';
  source: 'organic' | 'social' | 'referral' | 'direct';
  page: string;
}) => {
  if (typeof window !== 'undefined' && hasConsent('analytics')) {
    // Use requestIdleCallback for non-critical SEO tracking
    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(() => {
        sendSEOEvent(action, source, page);
      });
    } else {
      // Fallback
      setTimeout(() => sendSEOEvent(action, source, page), 100);
    }
  }
};

// Helper function to send SEO events
const sendSEOEvent = (action: string, source: string, page: string) => {
  if (window.gtag) {
    window.gtag('event', 'seo_event', {
      seo_action: action,
      seo_source: source,
      page_path: page,
      send_to: GA_MEASUREMENT_ID
    });
  }
};
